import { jwtDecode } from 'jwt-decode';

/**
 * The attributes of a Verify id_token that
 * we will use
 */
interface IdToken {
  displayName?: string;
  preferred_username?: string;
  sub?: string;
}

/**
 * Extended token claims in our app
 */
interface IdTokenExt extends IdToken {
  tid?: string;
}

/**
 * User info for consumption in the client
 */
export class UserInfo {
  /**
   * User's display name
   * e.g. "JOHN DOE"
   */
  displayName?: string;

  /**
   * User's preferred username
   * e.g "john.doe@acme.com"
   */
  userName?: string;

  /**
   * The identifier of the user's entitlement to the application
   * e.g. "111"
   */
  tenantId?: string;

  /**
   * should be the user's IBMid uid
   * e.g. "0000000AAA"
   */
  sub?: string | undefined;

  constructor(token: IdTokenExt) {
    this.sub = token.sub;
    this.userName = token.preferred_username;
    this.displayName = token.displayName;
    this.tenantId = token.tid;
  }
}

/**
 * Encapsulates the state of user session obtained from
 * the backend /auth/session endpoint
 *
 * This can be extended to contain session data e.g.
 * - user info (decoded identity token)
 * - entitlements
 */
export abstract class Session {
  /**
   * If the session has not been fetched, it is in an init state (e.g. first render)
   * @returns true if the session has been fetched
   */
  abstract isFetched(): boolean;

  /**
   * The session is valid if it is not expired. Token can expire before session expiry and be refreshed.
   * @returns true if the session is valid
   */
  abstract isValid(): boolean;

  /**
   * Token can expire before session expiry and be refreshed.
   * @returns true if the token currently held in session is valid
   */
  abstract isTokenValid(): boolean;

  /**
   * Is the session in an error state
   * @returns true if the session is in an error state
   */
  abstract isError(): boolean;

  /**
   * Is the session's error state a 401
   * @returns true if the session's error state is 401
   */
  abstract is401(): boolean;

  /**
   * Get string representation of error status
   */
  abstract error(): string;

  /**
   * Get the API authorization token
   */
  abstract getToken(): string | null;

  /**
   * Get user information
   */
  abstract getUserInfo(): UserInfo | null;
}

interface RequestStatus {
  code: number;
  text: string | null;
}

// internal implementation
// exported only for testability
export class SessionImpl extends Session {
  // fetched: boolean;
  errorStatus: RequestStatus | null;
  sessionStoreStatus: boolean;
  data: SessionData | null;

  constructor(
    // checked = false,
    data: SessionData | null = null,
    sessionStoreStatus = true,
    errorStatus: RequestStatus | null = null
  ) {
    super();
    // this.fetched = checked;
    this.data = data;
    this.sessionStoreStatus = sessionStoreStatus;
    this.errorStatus = errorStatus;
  }

  /**
   * Update the state of the session
   * returning a new object (reference) if the state
   * update was material (i.e. of interest to LoginProvider)
   *
   * @param data
   * @param sessionStoreStatus
   * @param errorStatus
   * @returns update session
   */
  set(
    data: SessionData | null,
    sessionStoreStatus: boolean,
    errorStatus: RequestStatus | null
  ): SessionImpl {
    const that = new SessionImpl(data, sessionStoreStatus, errorStatus);
    // const that = new SessionImpl(true, data, sessionStoreStatus, errorStatus);
    return this.equals(that) ? this : that;
  }

  /**
   * if checked: a call to /auth/session has been made
   * i.e. we are not in the init stage before any session call has been made
   * @returns true if session has been checked
   */
  isFetched(): boolean {
    return this.data !== null || this.errorStatus !== null;
  }

  isValid(): boolean {
    return this.isFetched() && (this.data?.isValid() || false);
  }

  isTokenValid(): boolean {
    return this.isFetched() && (this.data?.isTokenValid() || false);
  }

  isError(): boolean {
    return this.errorStatus != null;
  }

  is401(): boolean {
    return this.errorStatus?.code === 401;
  }

  error() {
    return JSON.stringify(this.errorStatus);
  }

  getToken() {
    return this.data?.accessToken || null;
  }

  getUserInfo(): UserInfo | null {
    let userInfo: UserInfo | null = null;
    if (this.data?.idToken) {
      const decoded = jwtDecode<IdTokenExt>(this.data.idToken);
      userInfo = new UserInfo(decoded);
    }
    return userInfo;
  }

  /**
   * Since Session will be used as a useEffect dependency
   * we want Object.is(newState, currentState) to return false
   * (i.e. be different references) when and only when the
   * internal state of the session is changed in a relevant
   * manner.
   *
   * We will encapsulate that internally in this module by
   * setting the stored session to new instance (reference)
   * whenever the internal state is updated in a relevant way.
   *
   * @returns true if this has the same relevant state as that
   */
  equals(that: SessionImpl): boolean {
    return (
      // this.fetched === that.fetched &&
      this.data === that.data &&
      this.sessionStoreStatus == that.sessionStoreStatus &&
      // we don't care about the content of the error
      // just if it exists or not
      (this.errorStatus === null
        ? that.errorStatus === null
        : that.errorStatus !== null)
    );
  }

  /**
   * @returns sanitized representation of the session
   */
  toString(): string {
    const dump = JSON.stringify(this);
    const o = JSON.parse(dump);
    if (o.data?.accessToken) {
      o.data.accessToken = '***';
    }
    if (o.data?.idToken) {
      o.data.idToken = '***';
      o['userInfo'] = this.getUserInfo();
    }
    return JSON.stringify(o);
  }
}
/**
 * SessionData utility
 * In its own class for testability
 */
export class SessionData {
  public accessToken: string;
  public idToken: string;
  public tokenExpires: Date;
  public expires: Date;

  public constructor(json: {
    access_token: string;
    id_token: string;
    tokenExpires: number;
    sessionExpires: number;
  }) {
    this.accessToken = json.access_token;
    this.idToken = json.id_token;
    this.tokenExpires = new Date(json.tokenExpires * 1000);
    this.expires = new Date(json.sessionExpires * 1000);
  }

  public isValid(): boolean {
    return this.expires.getTime() > Date.now();
  }

  public isTokenValid(): boolean {
    return this.tokenExpires.getTime() > Date.now();
  }
}
